import '~shared/components/Table/Table.scss';

/**
 * The custom instance of the useSocTable hook specifically for the WorkspaceQuizzesList
 */
import React from 'react';

import { Column, Row } from 'react-table';

import { Direction } from '~shared/utils/sort';
import { PaywallScreens } from '~analytics/types';
import { Action, ActionMenu } from '~shared/components/ActionMenu/ActionMenu';
import { Container } from '~shared/components/Container/Container';
import { sortByProperty } from '~shared/components/Table/sortingFns/sortByProperty/sortByProperty';
import { ActionCell } from '~shared/components/Table/update/components/ActionCell';
import { ActionHeader } from '~shared/components/Table/update/components/ActionHeader';
import { Checkbox } from '~shared/components/Table/update/components/Checkbox';
import { Sort } from '~shared/components/Table/update/components/Sort';
import { useSocTable } from '~shared/components/Table/update/useSocTable';
import { SharedLibraryUpgradeBanner } from '~teacher/components/workspaces/SharedLibraryUpgradeBanner';
import { WorkspaceItem } from '~teacher/hook/workspaces/useWorkspaceItems';
import { proCheck } from '~teacher/util/proCheck';

import {
  persistSortingRule,
  readPersistedSortingRule,
} from './sessionSortStorage';
import { WorkspaceItemIncrementMenu } from './WorkspaceItemIncrementMenu';
import { WorkspaceItemTableCell } from './WorkspaceItemTableCell';
import { WorkspaceItemTableFloatingActions } from './WorkspaceItemTableFloatingActions';
import { WorkspaceItemTableRow } from './WorkspaceItemTableRow';
import { WorkspaceItemTableWrapper } from './WorkspaceItemTableWrapper';

interface SortingRule<D> {
  id: keyof D & string;
  desc: boolean;
}
interface Props {
  data: Array<WorkspaceItem>;
  filters?: Array<
    (data: Array<WorkspaceItem>, search?: string) => Array<WorkspaceItem>
  >;
  searchQuery?: { id: string; value: string };
  actions?: Array<Action<WorkspaceItem>>;
  onSelection?: (data) => void;
  workspaceId: string;
  initialSort: SortingRule<WorkspaceItem>;
  children?: React.ReactNode;
  emptyState: (data: Array<Row<WorkspaceItem>>) => JSX.Element;
  columns?: ReadonlyArray<Column<WorkspaceItem>>;
  enableBulkActions: boolean;
  sortStorageKey?: string;
}

const DEFAULT_TABLE_INCREMENT = 50;
const staticSort = (a, b) => sortByProperty('type')(a.original, b.original);
const validSortIds = ['name', 'modified'];

export function WorkspaceItemTable({
  actions,
  data,
  searchQuery,
  onSelection,
  filters,
  children,
  initialSort,
  emptyState,
  columns,
  enableBulkActions,
  sortStorageKey,
}: Props) {
  const canUseSharedLibraryQuizzes = proCheck.canUseSharedLibraryQuizzes();
  const resolvedInitialSort = React.useMemo(() => {
    if (!sortStorageKey) {
      return initialSort;
    }
    const persisted = readPersistedSortingRule(sortStorageKey, validSortIds);
    if (!persisted) {
      return initialSort;
    }
    return {
      id: persisted.column,
      desc: persisted.direction === Direction.Descending,
    } as SortingRule<WorkspaceItem>;
  }, [initialSort, sortStorageKey]);

  // sort that prevails no matter local sorts (like modified, etc.)
  const selectionColumn = React.useMemo(
    () => ({
      header: (getToggleAllRowsSelectedProps) => (
        <div className={'th-outskirts'}>
          <Checkbox {...getToggleAllRowsSelectedProps()} />
        </div>
      ),
      cell: ({ row }) => {
        return <Checkbox {...row.getToggleRowSelectedProps()} />;
      },
    }),
    []
  );
  // custom action column
  const actionColumn = React.useMemo(
    () => ({
      header: ({ selectedRows, actions }) => (
        <ActionHeader
          enableBulkActions={enableBulkActions}
          selectedFlatRows={selectedRows}
          actions={actions}
        />
      ),
      cell: ({ row, actions }) => <ActionCell row={row} actions={actions} />,
    }),
    [enableBulkActions]
  );
  // hook initializer
  const {
    resolvedData,
    incrementFn,
    prepareRow,
    maxRows,
    selectedRows,
    toggleAllRows,
    totalRows,
    props: { headerGroups },
    state: { currentSort },
  } = useSocTable({
    filters,
    data,
    initialSort: resolvedInitialSort,
    columns: columns,
    enableBulkActions: enableBulkActions,
    increment: DEFAULT_TABLE_INCREMENT,
    searchQuery,
    actions,
    sortable: true,
    actionColumn,
    selectionColumn,
    staticSort,
  });

  React.useEffect(() => {
    if (!sortStorageKey) {
      return;
    }

    if (currentSort.length === 0) {
      persistSortingRule(sortStorageKey, null);
      return;
    }

    const rule = currentSort[0] as SortingRule<WorkspaceItem>;
    persistSortingRule(sortStorageKey, {
      column: rule.id,
      direction: rule.desc ? Direction.Descending : Direction.Ascending,
    });
  }, [currentSort, sortStorageKey]);

  // now comes the actual render of the table
  return (
    <Container className={'relative'}>
      {children}
      <SharedLibraryUpgradeBanner
        canUseSharedLibraryQuizzes={canUseSharedLibraryQuizzes}
        paywallScreen={PaywallScreens.SharedLibraryListBanner}
      />
      <WorkspaceItemTableWrapper>
        {/* header row */}
        {headerGroups.map((headerGroup) => (
          <WorkspaceItemTableRow
            key={headerGroup.getHeaderGroupProps().key}
            selectedRows={selectedRows}
            header
          >
            {headerGroup.headers.map((column) => {
              return (
                <WorkspaceItemTableCell
                  key={column.getHeaderProps().key}
                  header
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  column={column}
                >
                  {column.id !== 'actions' && column.render('Header')}
                  <Sort
                    enabled={
                      column.canSort && column.isSorted && !column.doNotShowSort
                    }
                    descending={column.isSortedDesc}
                  />
                </WorkspaceItemTableCell>
              );
            })}
          </WorkspaceItemTableRow>
        ))}
        {/* body rows */}
        {resolvedData.map((row) => {
          prepareRow(row);
          return (
            <ActionMenu
              actions={actions}
              enableCount
              data={
                selectedRows.length > 0
                  ? selectedRows.map((i) => i.original)
                  : [row.original]
              }
              mode={'context'}
              direction={'bottom-start'}
              key={`action-${row.getRowProps().key}`}
            >
              <WorkspaceItemTableRow row={row} selectedRows={selectedRows}>
                {row.cells.map((cell) => (
                  <WorkspaceItemTableCell
                    key={cell.getCellProps().key}
                    column={cell.column}
                    onClick={() =>
                      onSelection ? onSelection(row.original) : {}
                    }
                  >
                    {cell.render('Cell')}
                  </WorkspaceItemTableCell>
                ))}
              </WorkspaceItemTableRow>
            </ActionMenu>
          );
        })}
      </WorkspaceItemTableWrapper>
      <WorkspaceItemIncrementMenu
        maxRows={maxRows}
        setMaxRows={incrementFn}
        totalRows={totalRows}
      />
      {emptyState?.(resolvedData)}
      <WorkspaceItemTableFloatingActions
        selectedRows={selectedRows}
        actions={actions}
        toggleAllRows={toggleAllRows}
      />
    </Container>
  );
}
